import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";
import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from "./cognito-config.js";

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_USER_POOL_ID,
  ClientId: COGNITO_CLIENT_ID,
});

const SESSION_COOKIE = "heredajo-session";

// ── Cookie helpers ───────────────────────────────────────────────────────────
//
// Lambda@Edge reads this cookie on viewer-request to decide whether to pass
// through to the origin or 302 to /login. Cognito's amazon-cognito-identity-js
// only manages localStorage; we mirror the idToken into a cookie here so the
// edge function can see it.

// Domain=.heredajo.com so the cookie is sent to api.heredajo.com too;
// SameSite=None (required when the cookie crosses subdomains via XHR).
// In dev (localhost) we set neither so the browser keeps it host-only.
const COOKIE_DOMAIN_ATTR = window.location.hostname.endsWith("heredajo.com")
  ? "; Domain=.heredajo.com; SameSite=None"
  : "; SameSite=Lax";

export function setSessionCookie(idToken: string, expiresAt: Date): void {
  const expires = expiresAt.toUTCString();
  // Note: not HTTP-only — has to be set from JS. Trade-off accepted for a
  // small private site; XSS surface is small (React escapes by default; we
  // never use dangerouslySetInnerHTML).
  document.cookie = `${SESSION_COOKIE}=${idToken}; expires=${expires}; path=/${COOKIE_DOMAIN_ATTR}; Secure`;
}

export function clearSessionCookie(): void {
  document.cookie = `${SESSION_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${COOKIE_DOMAIN_ATTR}; Secure`;
}

export function readSessionCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${SESSION_COOKIE}=([^;]+)`));
  return match?.[1] ?? null;
}

/**
 * Synchronous heuristic for "does the client have a session?" — used by the
 * React route gate so we don't even try to render an authenticated route
 * (and trigger a /data/* fetch that the edge will then 302 to /login,
 * producing an HTML response that the JSON parser chokes on).
 *
 * Truth lives at the edge (Lambda@Edge validates the JWT against Cognito's
 * JWKS on every request). This is just a client-side fast path; if it
 * returns true but the token is actually expired, the loader's
 * content-type guard will catch it and re-redirect.
 */
export function hasSessionCookie(): boolean {
  return readSessionCookie() !== null;
}

// ── Session ──────────────────────────────────────────────────────────────────

export function getCurrentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

export async function getSession(): Promise<CognitoUserSession | null> {
  const user = getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
      } else {
        resolve(session);
      }
    });
  });
}

export async function getCurrentUserEmail(): Promise<string | null> {
  const session = await getSession();
  if (!session) return null;
  // The email lives in the idToken's payload.
  const payload = session.getIdToken().decodePayload() as { email?: string };
  return payload.email ?? null;
}

function persistSessionFromCognito(session: CognitoUserSession): void {
  const idToken = session.getIdToken();
  const expiresAt = new Date(idToken.getExpiration() * 1000);
  setSessionCookie(idToken.getJwtToken(), expiresAt);
}

// ── Sign in / sign out ───────────────────────────────────────────────────────

export class AuthError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly raw: unknown,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

function toAuthError(err: unknown): AuthError {
  if (err && typeof err === "object" && "message" in err) {
    const e = err as { message: string; code?: string; name?: string };
    return new AuthError(e.message, e.code ?? e.name ?? "Unknown", err);
  }
  return new AuthError(String(err), "Unknown", err);
}

export interface SignInResult {
  kind: "ok" | "newPasswordRequired";
  session?: CognitoUserSession;
  user?: CognitoUser;
  requiredAttributes?: string[];
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        persistSessionFromCognito(session);
        resolve({ kind: "ok", session, user });
      },
      onFailure: (err) => reject(toAuthError(err)),
      newPasswordRequired: (_userAttributes, requiredAttributes) => {
        // Admin-created users hit this on first sign-in with a temp password.
        resolve({ kind: "newPasswordRequired", user, requiredAttributes });
      },
    });
  });
}

export async function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => {
          persistSessionFromCognito(session);
          resolve(session);
        },
        onFailure: (err) => reject(toAuthError(err)),
      },
    );
  });
}

export async function signOut(): Promise<void> {
  const user = getCurrentUser();
  if (user) {
    await new Promise<void>((resolve) => {
      user.signOut(() => resolve());
    });
  }
  clearSessionCookie();
}

// ── Sign up ──────────────────────────────────────────────────────────────────

export async function signUp(email: string, password: string): Promise<ISignUpResult> {
  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, [], [], (err, result) => {
      if (err || !result) reject(toAuthError(err));
      else resolve(result);
    });
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) reject(toAuthError(err));
      else resolve();
    });
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) reject(toAuthError(err));
      else resolve();
    });
  });
}

// ── Password reset ───────────────────────────────────────────────────────────

export async function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
      inputVerificationCode: () => resolve(), // code-sent state
    });
  });
}

export async function confirmPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });
}

// ── Approval detection ───────────────────────────────────────────────────────

/**
 * Cognito surfaces our PreAuth-trigger error as
 *   `PreAuthentication failed with error Pending administrator approval.`
 * Detect this shape so the UI can show a friendly "awaiting approval"
 * state rather than a generic error.
 */
export function isPendingApprovalError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /Pending administrator approval/i.test(err.message);
}
